import type { RestaurantSettings } from "@/lib/api/types";

/**
 * WHICH stored footer this slip prints (D82).
 *
 * The settings model used to hold ONE footer string, printed verbatim under
 * every receipt in every language. Every Egyptian store types «شكرًا لزيارتكم»
 * into it, so an English receipt handed to a tourist — a document that also
 * carries the tax registration number — ended with a line they could not read.
 * The settings page could only WARN the owner about it, which is a disclosure,
 * not a fix.
 *
 * There are now two stored lines and this is the one rule that picks between
 * them. Its own tested function rather than a ternary inside each slip: there
 * are TWO slips (the bill and the refund) and a rule duplicated across two
 * customer-facing tax documents is a rule that will diverge — which is the
 * exact defect the swap label beside it had.
 *
 * **The English footer FALLS BACK to the Arabic one, never the reverse.** NULL
 * on `receiptFooterEn` means "this store has not written one", and printing the
 * Arabic line there is precisely what ships today — so a store that upgrades
 * and types nothing sees no change at all, and no receipt ever loses a footer
 * it used to have. An empty STRING is treated as absent too: the settings form
 * coerces "" to null on save, but a row saved by some other path must not print
 * a blank line where a sentence belongs.
 *
 * The Arabic slip never falls back to the English one. An Arabic-first document
 * (D50/S12) with an English-only footer is a store that has not written the
 * line its main audience reads, and quietly substituting the other language
 * would hide that from the owner.
 */
export function receiptFooterForLocale(
  settings:
    | Pick<RestaurantSettings, "receiptFooter" | "receiptFooterEn">
    | null
    | undefined,
  locale: "ar" | "en",
): string | null {
  const ar = blankToNull(settings?.receiptFooter);
  if (locale !== "en") {
    return ar;
  }
  return blankToNull(settings?.receiptFooterEn) ?? ar;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : (value ?? null);
}
